import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { Control, FieldPath } from 'react-hook-form';

import { ChevronDownIcon, SearchIcon } from '../../../assets/icons/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData } from './add-dividend.validation';

type Option = { label: string; value: string };

type Props = {
  control: Control<AddDividendFormData>;
  disabled?: boolean;
  /** Shown inside the dropdown when nothing is selectable — an empty list reads as a dead field. */
  emptyMessage?: ReactNode;
  name: FieldPath<AddDividendFormData>;
  onSelect?: (value: string) => void;
  options: Option[];
  placeholder: ReactNode;
};

// The stakeholder field the payout is built around, matching the picker the event forms use: the
// list is searched in place rather than scrolled. Options are the payout's own holders, already
// loaded and bounded by its share classes, so the filtering is local — no query per keystroke.
export const DividendStakeholderCombobox: FC<Props> = ({
  control,
  disabled,
  emptyMessage,
  name,
  onSelect,
  options,
  placeholder,
}) => {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;

    return options.filter(({ label }) => label.toLowerCase().includes(query));
  }, [options, search]);

  return (
    <FormCombobox
      className="relative w-full"
      control={control}
      name={name as never}
      onSelect={(value) => {
        setSearch('');
        onSelect?.(String(value));
      }}
    >
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={(value) =>
            options.find((option) => option.value === value)?.label ??
            (value ? undefined : getTranslation('PLEASE_SELECT'))
          }
          disabled={disabled}
          icon={<ChevronDownIcon className="size-4" />}
          name={name as never}
          placeholder={placeholder}
          readOnly
        />
        {!disabled && <ComboboxButton className="absolute inset-0 z-1 size-full cursor-pointer" />}
      </div>
      <ComboboxOptions className="w-full gap-0.5 p-1">
        <div
          className="relative flex w-full items-center"
          onClick={() => searchRef.current?.focus()}
        >
          <SearchIcon className="absolute ml-[6px]" />
          <input
            className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
            onChange={({ target: { value } }) => setSearch(value)}
            placeholder={getTranslation('SEARCH')}
            ref={searchRef}
            value={search}
          />
        </div>
        {matches.length === 0 && (
          <span className="block px-3 py-1.5 text-sm text-gray-500">
            {search ? getTranslation('NO_RESULTS_FOUND') : emptyMessage}
          </span>
        )}
        {matches.map((option) => (
          <FormComboboxOption
            control={control}
            key={option.value}
            name={name as never}
            value={option.value as never}
          >
            <span className="block truncate text-sm text-gray-700">{option.label}</span>
          </FormComboboxOption>
        ))}
      </ComboboxOptions>
    </FormCombobox>
  );
};
